/**
 * Favorilerim sayfası (sections/holly-wishlist.liquid).
 *
 * Favori listesi Holly Wishlist uygulamasının app proxy endpoint'lerinden okunur
 * (üye: /favorite/page, misafir: localStorage + /product/list). Kartlar PLP ile
 * birebir aynı olsun diye her ürün için tema'nın product-card snippet'i Section
 * Rendering API ile çekilir (sections/api-wishlist-card.liquid).
 *
 * Aynı ürünün birden fazla varyantı favorideyse tek kart gösterilir; kalpten
 * çıkarınca ürünün tüm favori varyantları silinir.
 */
if (!customElements.get('hp-wishlist')) {
  customElements.define(
    'hp-wishlist',
    class HpWishlist extends HTMLElement {
      static STORAGE_KEY = 'hollyWishListProducts';
      static CARD_SECTION_ID = 'api-wishlist-card';
      static STORAGE_SYNC_TIMEOUT = 3000;

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.customerId = this.dataset.customerId || '';
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.emptyState = this.querySelector('[data-wishlist-empty]');
        this.errorState = this.querySelector('[data-wishlist-error]');
        this.pagination = this.querySelector('[data-wishlist-pagination]');
        this.prevButton = this.querySelector('[data-wishlist-prev]');
        this.nextButton = this.querySelector('[data-wishlist-next]');
        this.skeletonHTML = this.grid.innerHTML;
        this.pageInfo = null;
        this.requestId = 0;

        // Misafir favorisi kapalıysa uygulamanın kendi sayfası gibi girişe yönlendir
        if (!this.customerId && typeof favoriteWithoutMembership !== 'undefined' && !favoriteWithoutMembership) {
          window.location.assign(`/account/login?return_url=${window.location.pathname}`);
          return;
        }

        this.grid.addEventListener('click', this.onHeartActivate.bind(this), true);
        this.grid.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') this.onHeartActivate(event);
        });
        this.prevButton.addEventListener('click', () => this.goToPage('before', this.pageInfo?.previousPageCursor));
        this.nextButton.addEventListener('click', () => this.goToPage('after', this.pageInfo?.nextPageCursor));
        window.addEventListener('popstate', () => this.load());

        this.load();
      }

      async load() {
        const requestId = ++this.requestId;
        this.setState('loading');

        try {
          const { items, pageInfo } = await this.fetchFavorites();
          const entries = this.groupByProduct(items);
          const cards = await Promise.all(entries.map((entry) => this.fetchCard(entry)));
          if (requestId !== this.requestId) return;

          this.pageInfo = pageInfo;
          this.render(entries, cards);
        } catch (error) {
          console.error('[hp-wishlist]', error);
          if (requestId === this.requestId) this.setState('error');
        }
      }

      async fetchFavorites() {
        if (this.customerId) {
          await this.waitForStorageSync();

          const params = new URLSearchParams(window.location.search);
          const cursorKey = ['after', 'before'].find((key) => params.get(key));
          const query = cursorKey ? `?${cursorKey}=${encodeURIComponent(params.get(cursorKey))}` : '';
          const response = await this.request(`/apps/holly-wishlist/favorite/page${query}`);
          const data = response?.data;

          return { items: data?.items ?? data ?? [], pageInfo: data?.pageInfo ?? null };
        }

        const stored = this.readStorage().filter((item) => item.productId);
        if (!stored.length) return { items: [], pageInfo: null };

        const response = await this.request('/apps/holly-wishlist/product/list', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            products: stored.map((item) => ({
              productId: item.productId,
              productVariantId: item.productVariantId || null,
            })),
          }),
        });

        return { items: response?.data ?? [], pageInfo: null };
      }

      /**
       * Üye girişinden hemen sonra uygulama embed'i misafirken eklenen favorileri
       * (localStorage) window load'da sunucuya aktarıp storage'ı temizler. Liste
       * eksik gelmesin diye o aktarımı kısa bir süre bekler.
       */
      async waitForStorageSync() {
        if (this.storageSynced) return;
        this.storageSynced = true;

        const deadline = Date.now() + HpWishlist.STORAGE_SYNC_TIMEOUT;
        while (this.readStorage().length && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
      }

      groupByProduct(items) {
        const entries = new Map();

        for (const item of Array.isArray(items) ? items : []) {
          const product = item?.product;
          if (!product?.handle || !product.shopifyId) continue;

          const productId = String(product.shopifyId);
          const variantId = product.variantShopifyId ? String(product.variantShopifyId) : null;

          if (!entries.has(productId)) {
            entries.set(productId, { productId, handle: product.handle, variantId, variantIds: [] });
          }
          if (variantId) entries.get(productId).variantIds.push(variantId);
        }

        return [...entries.values()];
      }

      async fetchCard(entry) {
        const params = new URLSearchParams({ section_id: HpWishlist.CARD_SECTION_ID });
        if (entry.variantId) params.set('variant', entry.variantId);

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}products/${encodeURIComponent(entry.handle)}?${params}`);
          if (!response.ok) return null;

          const html = await response.text();
          return new DOMParser().parseFromString(html, 'text/html').querySelector('product-card');
        } catch (error) {
          console.error('[hp-wishlist]', error);
          return null;
        }
      }

      render(entries, cards) {
        const fragment = document.createDocumentFragment();

        entries.forEach((entry, index) => {
          const card = cards[index];
          if (!card) return;

          this.prepareCard(card, entry);
          fragment.append(card);
        });

        this.grid.replaceChildren(fragment);
        this.setState(this.grid.children.length ? 'ready' : 'empty');
        this.updatePagination();
      }

      prepareCard(card, entry) {
        card.wishlistEntry = entry;

        const heart = card.querySelector('.holly-wishlist-button');
        if (!heart) return;

        // Uygulamanın toggle'ı yerine bu sayfanın kaldırma akışı çalışır
        heart.removeAttribute('onclick');
        heart.dataset.favorite = 'true';
        heart.setAttribute('role', 'button');
        heart.setAttribute('tabindex', '0');
        heart.setAttribute('aria-label', this.dataset.removeLabel || '');
        heart.querySelector('svg')?.setAttribute('fill', '#000000');
      }

      onHeartActivate(event) {
        const heart = event.target.closest('.holly-wishlist-button');
        if (!heart || !this.grid.contains(heart)) return;

        event.preventDefault();
        event.stopPropagation();
        this.removeCard(heart.closest('product-card'));
      }

      async removeCard(card) {
        const entry = card?.wishlistEntry;
        if (!entry || card.classList.contains('is-removing')) return;

        card.classList.add('is-removing');

        try {
          await this.removeFavorite(entry);
        } catch (error) {
          console.error('[hp-wishlist]', error);
          card.classList.remove('is-removing');
          return;
        }

        card.remove();
        if (this.grid.children.length) return;

        // Sayfalı listede sayfa boşaldıysa ilk sayfayı yeniden yükle
        if (this.pageInfo?.hasPreviousPage || this.pageInfo?.hasNextPage) {
          window.history.replaceState({}, '', window.location.pathname);
          this.load();
        } else {
          this.setState('empty');
          this.updatePagination();
        }
      }

      async removeFavorite(entry) {
        if (this.customerId) {
          const variantIds = entry.variantIds.length ? entry.variantIds : [null];
          await Promise.all(
            variantIds.map((variantId) =>
              this.request('/apps/holly-wishlist/favorite/remove', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: entry.productId, productVariantId: variantId }),
              })
            )
          );
          return;
        }

        const stored = this.readStorage().filter((item) => String(item.productId) !== entry.productId);
        localStorage.setItem(HpWishlist.STORAGE_KEY, JSON.stringify(stored));
      }

      goToPage(direction, cursor) {
        if (!cursor) return;

        const url = new URL(window.location.href);
        url.searchParams.delete('after');
        url.searchParams.delete('before');
        url.searchParams.set(direction, cursor);
        window.history.pushState({}, '', url);

        this.load();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      updatePagination() {
        const hasPrevious = Boolean(this.pageInfo?.hasPreviousPage);
        const hasNext = Boolean(this.pageInfo?.hasNextPage);

        this.pagination.hidden = !(hasPrevious || hasNext);
        this.prevButton.disabled = !hasPrevious;
        this.nextButton.disabled = !hasNext;
      }

      setState(state) {
        this.dataset.state = state;

        if (state === 'loading') {
          this.grid.innerHTML = this.skeletonHTML;
          this.pagination.hidden = true;
        }

        this.grid.setAttribute('aria-busy', String(state === 'loading'));
        this.grid.hidden = state === 'empty' || state === 'error';
        this.emptyState.hidden = state !== 'empty';
        this.errorState.hidden = state !== 'error';
      }

      readStorage() {
        try {
          const stored = JSON.parse(localStorage.getItem(HpWishlist.STORAGE_KEY));
          return Array.isArray(stored) ? stored : [];
        } catch {
          return [];
        }
      }

      async request(url, options) {
        const response = await fetch(url, options);
        if (!response.ok) throw new Error(`${url} → HTTP ${response.status}`);

        const text = await response.text();
        const json = text ? JSON.parse(text) : null;
        if (json?.isSuccess === false) throw new Error(`${url} → ${json.message || 'isSuccess: false'}`);

        return json;
      }
    }
  );
}
